'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Download, Share2, Bookmark, X } from 'lucide-react';
import { UnifiedMomentResult } from '@/lib/services/unified-search-service';
import { MuxClipPlayer } from './MuxClipPlayer';
import { SaveToCollectionButton } from '@/components/collections/SaveToCollectionButton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface MomentModalProps {
  moment: UnifiedMomentResult | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MOMENT_TYPE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  chapter: { label: 'Chapter', color: 'bg-blue-500', icon: '📖' },
  highlight: { label: 'Highlight', color: 'bg-yellow-500', icon: '✨' },
  quote: { label: 'Quote', color: 'bg-apprendo-500', icon: '💬' },
  topic: { label: 'Topic', color: 'bg-green-500', icon: '🏷️' },
  action: { label: 'Action', color: 'bg-red-500', icon: '✅' },
  faq: { label: 'Q&A', color: 'bg-indigo-500', icon: '❓' },
  sentiment: { label: 'Sentiment', color: 'bg-pink-500', icon: '😊' },
  playbook: { label: 'Playbook', color: 'bg-orange-500', icon: '📋' },
  // B2B Marketing types
  testimonial: { label: 'Testimonial', color: 'bg-emerald-500', icon: '💚' },
  case_study: { label: 'Case Study', color: 'bg-cyan-500', icon: '🏆' },
  pain_point: { label: 'Pain Point', color: 'bg-rose-500', icon: '⚠️' },
  objection_handling: { label: 'Objection', color: 'bg-violet-500', icon: '🛡️' },
  social_proof: { label: 'Social Proof', color: 'bg-amber-500', icon: '📈' },
};

interface TranscriptSegment {
  text: string;
  speaker?: string;
  startTime: number;
  endTime: number;
}

export function MomentModal({ moment, open, onOpenChange }: MomentModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);

  useEffect(() => {
    if (moment && open) {
      // Fetch transcript for the moment
      fetchTranscript(moment.postId, moment.startTime, moment.endTime);
    }
  }, [moment, open]);

  const fetchTranscript = async (postId: string, startTime: number, endTime: number) => {
    try {
      const response = await fetch(
        `/api/moments/transcript?postId=${postId}&start=${startTime}&end=${endTime}`
      );
      if (response.ok) {
        const data = await response.json();
        setTranscriptSegments(data.segments || []);
      }
    } catch (error) {
      console.error('Error fetching transcript:', error);
    }
  };

  const handleExport = async () => {
    if (!moment) return;

    setIsExporting(true);
    try {
      const response = await fetch('/api/moments/export-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId: moment.postId,
          startTime: moment.startTime,
          endTime: moment.endTime,
          title: moment.title || moment.description,
          includeCaptions: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Export started! You\'ll be notified when it\'s ready.');
      } else {
        toast.error('Failed to start export');
      }
    } catch (error) {
      console.error('Error exporting moment:', error);
      toast.error('Failed to export moment');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = () => {
    if (!moment) return;

    // Prefer moment-id-based deeplinks — they survive multi-segment
    // compilations (a single ?t= can't express N disjoint ranges). The
    // `moment.id` is the canonical id when present; fall back to
    // momentId (legacy alias) or finally the timestamp-based form for
    // moments that have no id (auto-gen entries living in
    // video_posts.moments_data only).
    const momentIdForLink = (moment as any).id || (moment as any).momentId;
    const url = momentIdForLink
      ? `${window.location.origin}/dashboard/posts/${moment.postId}?moment=${momentIdForLink}`
      : `${window.location.origin}/dashboard/posts/${moment.postId}?t=${moment.startTime}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  if (!moment) return null;

  const typeConfig = moment.momentType ? MOMENT_TYPE_CONFIG[moment.momentType] : null;

  // Calculate context: show 30s before and after
  const contextStartTime = Math.max(0, moment.startTime - 30);
  const contextEndTime = moment.endTime + 30;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] w-full h-[90vh] p-0 gap-0">
        {/* Close button - top right */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-50 text-white hover:bg-white/10 bg-black/50 backdrop-blur"
        >
          <X className="h-5 w-5" />
        </Button>

        <div className="flex flex-col h-full">
          {/* Video Player - Takes most of the space */}
          <div className="flex-1 bg-black flex items-center justify-center">
            {moment.muxPlaybackId ? (
              <MuxClipPlayer
                playbackId={moment.muxPlaybackId}
                startTime={contextStartTime}
                endTime={contextEndTime}
                controls={true}
                autoPlay={true}
                className="w-full h-full"
              />
            ) : (
              <div className="text-white text-center">
                <p>Video not available</p>
              </div>
            )}
          </div>

          {/* Info Bar Below Video */}
          <div className="bg-background border-t">
            {/* Title and metadata */}
            <div className="px-6 py-4 border-b">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-1">
                    {moment.title || moment.description}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{formatTimestamp(moment.startTime)} - {formatTimestamp(moment.endTime)}</span>
                    <span>•</span>
                    <span>{Math.max(1, Math.round(moment.endTime - moment.startTime))}s</span>
                    {moment.engagementScore && (
                      <>
                        <span>•</span>
                        <span>{(moment.engagementScore * 100).toFixed(0)}% engagement</span>
                      </>
                    )}
                    {moment.speaker && (
                      <>
                        <span>•</span>
                        <span>{moment.speaker}</span>
                      </>
                    )}
                  </div>
                </div>
                {typeConfig && (
                  <Badge className={cn('text-white', typeConfig.color)}>
                    <span className="mr-1">{typeConfig.icon}</span>
                    {typeConfig.label}
                  </Badge>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleExport}
                  disabled={isExporting}
                  variant="default"
                  size="sm"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isExporting ? 'Exporting...' : 'Export Clip'}
                </Button>

                <SaveToCollectionButton
                  momentId={moment.id}
                  postId={moment.postId}
                  startTime={moment.startTime}
                  endTime={moment.endTime}
                  title={moment.title}
                  description={moment.description}
                  variant="outline"
                  size="sm"
                />

                <Button
                  onClick={handleShare}
                  variant="outline"
                  size="sm"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              </div>
            </div>

            {/* Transcript */}
            <div className="px-6 py-4 max-h-[200px] overflow-y-auto">
              <h3 className="font-semibold mb-3 text-sm">Transcript</h3>
              {transcriptSegments.length > 0 ? (
                <div className="space-y-3">
                  {transcriptSegments.map((segment, index) => (
                    <div key={index} className="space-y-1">
                      {segment.speaker && (
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {segment.speaker}
                        </div>
                      )}
                      <p className="text-sm leading-relaxed text-foreground">
                        {segment.text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  Loading transcript...
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatTimestamp(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
